<script setup lang="ts">
useHead({ title: 'Směnárenský software' })

const contactRef = ref()
function scrollToContact () {
  contactRef.value.scrollIntoView({ behavior: 'smooth' })
}
const cryptoHighlights = [
  {
    title: 'Konkurenční výhoda',
    text: 'Mít vlastní směnárnu vám poskytuje konkurenční výhodu, kterou můžete využít k oslovení nových klientů.',
    icon: 'i-fubex-run',
  },
  {
    title: 'Flexibilita',
    text: 'S vlastní směnárnou můžete být mnohem flexibilnější v tom, jaké kryptoměny a jaké směnné kurzy nabízíte.',
    icon: 'i-fubex-flexibility',
  },
  {
    title: 'Důvěra',
    text: 'Směnárna kryptoměn ve firemních barvách a na vlastní doméně zvýší důvěru u vašich klientů a posílí vaši reputaci.',
    icon: 'i-fubex-trust',
  },
  {
    title: 'Ziskovost',
    text: 'Směnárenské poplatky jsou zdrojem vašeho zisku a mít vlastní směnárnu vám umožňuje profitovat na každé transakci.',
    icon: 'i-fubex-income',
  },
]
const HowItWorks = [
  { title: 'Finanční poradci' }, { title: 'Tradeři s komunitou' }, { title: 'Krypto a NFT projekty' }, { title: 'Online deníky' }, { title: 'Realitní makléři' }, { title: 'Online podnikatelé' },
]
const WhatIsGoodToKnow = [
  { title: 'Okamžitá verifikace', text: 'Ověření uživatelů za pár vteřin. S umělou inteligencí to není problém!' },
  { title: 'Vypořádání směn', text: 'Spolehněte se na naše zkušené pracovníky, kteří zajišťují plynulé zpracování všech transakcí.' },
  { title: 'Žádné rezervy', text: 'K vypořádání transakcí využíváme vlastní zdroje pro zabezpečení rychlé směny.' },
  { title: 'Rychlá realizace', text: 'Po dodání potřebných podkladů je spuštění vlastní směnárny realitou již za několik dní.' },
]
</script>

<template>
  <div>
    <div bg-gradient-to-br dark:bg-gradient-to-b from="primary-200 dark:neutral-50" via-primary-50 dark="via-50% via-primary-50" to="primary-100 dark:neutral-100">
      <div fubex-container>
        <div :data-test="getCypressId('body-header')" grid grid-cols="1 md:2" items-center gap-68px md:gap-24px py-64px>
          <div paragraph-md text-neutral-900 flex flex-col gap-8px>
            <h1 display-sm sm:display-lg font-700 text-neutral-900>
              Automatizovaný směnárenský software
            </h1>
            <p mt-24px mb-16px max-w-480px>
              Získejte online směnárnu kryptoměn ve firemních barvách a na vlastní doméně. Umožněte své komunitě směňovat kryptoměny jednoduše a za výhodné kurzy.
            </p>
            <p mb-32px max-w-480px>
              Po dodání potřebných podkladů vám spustíme směnárnu do pár dní.
            </p>
            <div flex gap-12px flex-col md:flex-row>
              <FButton :data-test="getCypressId('more-button')" name="whitelabel-consultation" size="lg" px-60px @click="scrollToContact">
                Konzultace zdarma
              </FButton>
            </div>
          </div>
          <div>
            <NuxtImg :src="`${'/img/macbook.webp'}`" alt="" relative z-1 />
          </div>
        </div>
      </div>
    </div>
    <div :data-test="getCypressId('widgets')" class="light" w-full bg-neutral-900 py-60px mb-80px lg:mb-120px>
      <div class="fubex-container">
        <div grid class="grid-cols-[repeat(auto-fit,minmax(200px,270px))]" place-items-center justify-center text-center gap-32px sm:gap-40px>
          <WidgetOverlimitHighlight v-for="(item, index) in cryptoHighlights" :key="index" :title="item.title" :text="item.text" :icon="item.icon" />
        </div>
      </div>
    </div>
    <div class="fubex-container">
      <div flex flex-col gap-48px md:gap-82px>
        <div :data-test="getCypressId('for-who')" grid grid-cols="1 md:2" gap-56px>
          <div place-self-center order="md:1 2" relative>
            <Ellipse />
            <NuxtImg :src="`${'/img/tower.webp'}`" alt="" max-w-450px w="80%" md:w-full m-auto mt="-10px" relative z-10 />
          </div>
          <div order="md:2 1" z-10>
            <div flex flex-col gap-24px items-start text-left>
              <h2 heading1 text-neutral-900 font-700>
                Pro koho je vlastní směnárna vhodná
              </h2>
              <h3 text-neutral-700 paragraph-md max-w-648px>
                Jednoduchý a snadno použitelný software.
              </h3>
              <ul flex flex-col gap-8px>
                <LiWithCheck v-for="(item, index) in HowItWorks" :key="index">
                  {{ item.title }}
                </LiWithCheck>
              </ul>
            </div>
          </div>
        </div>
        <div :data-test="getCypressId('good-to-know')" grid grid-cols="1 md:2" gap-48px md:gap-4px>
          <div place-self-center order-2 relative>
            <Ellipse />
            <NuxtImg :src="`${'/img/slice.webp'}`" alt="" max-w-328px relative z-10 />
          </div>
          <div z-10>
            <div flex flex-col gap-24px items-start text-left order-1>
              <h2 heading1 text-neutral-900 font-700>
                Co je dobré vědět?
              </h2>
              <ul list-disc flex flex-col gap-18px text-left ml-24px>
                <div v-for="(item, index) in WhatIsGoodToKnow" :key="index" flex flex-col>
                  <li font-700>
                    {{ item.title }}
                  </li>
                  {{ item.text }}
                </div>
              </ul>
            </div>
          </div>
        </div>
        <div :data-test="getCypressId('software-use')" fubex-container text-center mb-82px>
          <h2 :data-test="getCypressId('software-use-header')" heading1 text-neutral-900 font-700 my-32px md:my-48px>
            Kde se používá náš software?
          </h2>
          <WidgetReferencesCarousel @scroll-to-contact="scrollToContact()" />
        </div>
      </div>
    </div>
    <div ref="contactRef" />
    <WidgetContactForm>
      <h2 heading2 font-700 text-white my-12px>
        Konzultace zdarma
      </h2>
      <div text-white>
        Zaujala Vás možnost vlastní firemní směnárny?
      </div>
      <div text-white>
        Zanechte nám Váš kontakt a my se Vám ozveme!
      </div>
    </WidgetContactForm>
  </div>
</template>
